import React, { useCallback, useState, Suspense, lazy, Component } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { getAdminRouteLabel, type AdminNavigationContext, type AdminRoute } from './adminNavigation';
// Lazy-loaded admin pages — each becomes its own bundle chunk so initial admin
// payload stays small and a single failing page can't crash the whole portal.
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({
  default: m.UsersPage
})));
const QuestionBankPage = lazy(() => import('./pages/QuestionBankPage').then(m => ({
  default: m.QuestionBankPage
})));
const GovernancePage = lazy(() => import('./pages/GovernancePage').then(m => ({
  default: m.GovernancePage
})));
const SupportContentPage = lazy(() => import('./pages/SupportContentPage').then(m => ({
  default: m.SupportContentPage
})));
const DiagnosticsPage = lazy(() => import('./pages/DiagnosticsPage').then(m => ({
  default: m.DiagnosticsPage
})));
const LogsPage = lazy(() => import('./pages/LogsPage').then(m => ({
  default: m.LogsPage
})));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({
  default: m.SettingsPage
})));
const MascotPage = lazy(() => import('./pages/MascotPage').then(m => ({
  default: m.MascotPage
})));
const LdgGraphPage = lazy(() => import('./pages/LdgGraphPage').then(m => ({
  default: m.LdgGraphPage
})));
const SessionAnalyticsPage = lazy(() => import('./pages/SessionAnalyticsPage').then(m => ({
  default: m.SessionAnalyticsPage
})));
const SsotRegistryPage = lazy(() => import('./pages/SsotRegistryPage').then(m => ({
  default: m.SsotRegistryPage
})));
const CodeRegistryPage = lazy(() => import('./pages/CodeRegistryPage').then(m => ({
  default: m.CodeRegistryPage
})));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage').then(m => ({
  default: m.DesignSystemPage
})));
import { type AccountsState, type Account } from '../../accounts';
import { ModuleId, Question } from '../../content';
import { AppState } from '../../app/types';
import { Menu, X, Lock, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
const ADMIN_PASSWORD = 'djaymeester2026';
const ADMIN_UNLOCK_KEY = 'admin_unlocked';
export type { AdminRoute } from './adminNavigation';
/** Props passed to all admin page components via spread. */
type AdminPageProps = Omit<AdminShellProps, 'onNavigate'> & {
  navigationContext?: AdminNavigationContext | null;
  onNavigate: (route: AdminRoute, context?: AdminNavigationContext) => void;
};
const PAGE_MAP: Record<AdminRoute, ComponentType<Partial<AdminPageProps>>> = {
  dashboard: DashboardPage,
  users: UsersPage,
  'question-bank': QuestionBankPage,
  governance: GovernancePage,
  ssot: SsotRegistryPage,
  'code-registry': CodeRegistryPage,
  'design-system': DesignSystemPage,
  'support-content': SupportContentPage,
  'ldg-graph': LdgGraphPage,
  mascot: MascotPage,
  diagnostics: DiagnosticsPage,
  logs: LogsPage,
  settings: SettingsPage,
  analytics: SessionAnalyticsPage
};
interface AdminShellProps {
  accounts?: AccountsState;
  getLearnerData?: (learnerId: string) => AppState | null;
  getLearnerModuleMastery?: (learnerId: string, moduleId: ModuleId) => number;
  selectedGroup?: number;
  onNavigate?: (view: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  allAccounts?: Account[];
  activeAccountId?: string;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
  questions?: Question[];
  state?: AppState;
  onReturnHome?: () => void;
}
export function AdminShell(props: AdminShellProps) {
  const [activeRoute, setActiveRoute] = useState<AdminRoute>('dashboard');
  const [navigationContext, setNavigationContext] = useState<AdminNavigationContext | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem(ADMIN_UNLOCK_KEY) === 'true');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigateToRoute = useCallback((route: AdminRoute, context?: AdminNavigationContext) => {
    setNavigationContext(context ?? null);
    setActiveRoute(route);
    setMobileMenuOpen(false);
  }, []);
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      sessionStorage.setItem(ADMIN_UNLOCK_KEY, 'true');
      setIsUnlocked(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };
  if (!isUnlocked) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-4236" data-mp="2dfegwqpr06lj">
        <div className="w-full max-w-sm" data-id="element-4237" data-mp="1whyszquf27p">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8" data-id="element-4238" data-mp="3t5dylc0uu2sk">
            <div className="flex flex-col items-center mb-6" data-id="element-4239" data-mp="bo4g9dtrxxiw">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center shadow-md mb-4" data-id="element-4240" data-mp="bnl1u73c1r9m">
                <Lock className="w-6 h-6 text-white" data-id="element-4241" data-mp="3f4l3ap2qb3oe" />
              </div>
              <h1 className="text-lg font-bold text-gray-900" data-id="element-4242" data-mp="3a600qj0087ws">Admin Portaal</h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4243" data-mp="3a84f5uvj4pbb">
                Voer het wachtwoord in om verder te gaan
              </p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-id="element-4244" data-mp="3k3xwrso3v8zy">
              <div data-id="element-4245" data-mp="13faa5ciwiyzq">
                <div className="relative" data-id="element-4246" data-mp="13esi6s39v6xm">
                  <input type={showPassword ? 'text' : 'password'} value={passwordInput} onChange={e => {
                  setPasswordInput(e.target.value);
                  setPasswordError(false);
                }} placeholder="Wachtwoord" autoFocus className={`w-full px-4 py-3 pr-11 rounded-xl border-2 text-sm transition-colors focus:outline-none focus:ring-0 ${passwordError ? 'border-red-300 bg-red-50 focus:border-red-400' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} data-id="element-4247" data-mp="13dqvtrgt2y0l" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" tabIndex={-1} data-id="element-4248" data-mp="3eg94nhutm431">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-4249" data-mp="2qpddmmy6vcwu" /> : <Eye className="w-4 h-4" data-id="element-4250" data-mp="2uri6vkkq67iy" />}
                  </button>
                </div>
                {passwordError && <p className="text-xs text-red-500 mt-2 pl-1" data-id="element-4251" data-mp="2i9xmhda6tsa3">
                    Onjuist wachtwoord. Probeer het opnieuw.
                  </p>}
              </div>
              <button type="submit" disabled={!passwordInput.trim()} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm" data-id="element-4252" data-mp="299rbpz5vj6em">
                Inloggen
              </button>
            </form>
          </div>
          <p className="text-xs text-gray-400 text-center mt-4" data-id="element-4253" data-mp="1qkkpi25vfwo2">
            Alleen voor beheerders
          </p>
        </div>
      </div>;
  }
  const PageComponent = PAGE_MAP[activeRoute] ?? DashboardPage;
  return <div className="flex h-screen bg-gray-50 overflow-hidden" data-id="element-4254" data-mp="1glvhr27ovslj">
      {/* Skip to content — a11y (A11Y-01 fix) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-4255" data-mp="34fp1j1r119bu">
        Ga naar inhoud
      </a>

      {/* Mobile Header with safe area */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-4256" data-mp="2lrjwzs2jpkbz">
        <div className="font-bold text-gray-900 flex items-center gap-2.5 py-3" data-id="element-4257" data-mp="2cosljs9o6okc">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-4258" data-mp="2gq4ea6nubpwr">
            <span className="text-white font-black text-sm" data-id="element-4259" data-mp="27s26975ovwzz">M</span>
          </div>
          <span className="text-sm" data-id="element-4260" data-mp="1o5qx15utilpj">{getAdminRouteLabel(activeRoute)}</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 -mr-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-4261" data-mp="1o7k2ihtp2zvy">
          {mobileMenuOpen ? <X className="w-5 h-5" data-id="element-4262" data-mp="273tff1sv1ked" /> : <Menu className="w-5 h-5" data-id="element-4263" data-mp="272i9owfuu2km" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-gray-900/40 z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-4264" data-mp="2guekn831hn69" />

      {/* Sidebar - Desktop & Mobile Drawer */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-4265" data-mp="2lthd7t1u20xc">
        <AdminSidebar activeRoute={activeRoute} onNavigate={navigateToRoute} onLogout={props.onLogout} onReturnHome={props.onReturnHome} data-id="element-4266" data-mp="1eeat8xmucti1" />
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 min-h-0 overflow-hidden pt-14 md:pt-0" data-id="element-4267" data-mp="2703zxib9csa3">
        <BaseErrorBoundary logPrefix="Admin Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-4269" data-mp="1t1njgznlfner">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4" data-id="element-4270" data-mp="lpn4gs2vvlzq">
                <AlertCircle className="w-8 h-8 text-indigo-600" data-id="element-4271" data-mp="hlp7amx4cl9b" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4272" data-mp="2tbwz7ahwl1yn">
                Er ging iets mis
              </h2>
              <p className="text-sm text-gray-500 mb-4 max-w-md" data-id="element-4273" data-mp="2xcgxihe7qbmd">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-4274" data-mp="329h2sq38nacf">
                Probeer opnieuw
              </button>
            </div>} data-id="element-4268" data-mp="1x40o1kqltf9y">
          <Suspense fallback={<div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" role="status" aria-live="polite" data-id="element-4276" data-mp="3l1jowgynfiu2">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin mb-3" data-id="element-4277" data-mp="1qok3u8bi1wfe" />
                <p className="text-sm text-gray-500" data-id="element-4278" data-mp="1vmlnovgr1zrx">
                  {getAdminRouteLabel(activeRoute)} laden…
                </p>
              </div>} data-id="element-4275" data-mp="3h2arn24eh2tr">
            <PageComponent {...props} navigationContext={navigationContext} onNavigate={navigateToRoute} data-id="element-4279" data-mp="2yzlufu6himzs" />
          </Suspense>
        </BaseErrorBoundary>
      </main>
    </div>;
}